import type { ConnectMessage } from "@ando/core";
import { Composer, MessageBubble, MessageMeta, cn, toast } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { MessagesSquare } from "lucide-react";
import * as React from "react";

import type { StoredGuestCredential } from "../guestCredential.js";
import { usePostSpaceMessage, useSpaceMessages } from "../hooks/useSpaceMessages.js";
import { formatRelative } from "./connectShared.js";

/**
 * ConnectMessageThread — the member-to-member DISCUSSION surface of a Connect
 * Space (June 2026).
 *
 * A free-form, human-to-human conversation between the two collaborating
 * companies. It is DELIBERATELY a separate component from ``ConnectChat`` (the
 * scoped AI Q&A): that one streams one-shot LLM answers with citations and owns
 * its own ephemeral local turns; THIS one is a persisted, server-backed thread
 * between PEOPLE, polled near-real-time and posted optimistically. The two must
 * never be conflated — so this is NOT a fork of ConnectChat.
 *
 * It DOES reuse the SAME @ando/ui presentation primitives the main chat uses —
 * ``MessageBubble`` (the right-aligned accent pill for own / left transparent
 * for others), ``MessageMeta`` (the uppercase author · time row) and the
 * ``Composer`` family (cream card + auto-resize textarea + accent send) — so
 * the LOOK is identical to the rest of the product without duplicating bubble /
 * composer chrome. DRY at the primitive layer, distinct at the domain layer.
 *
 * Transport is identity-agnostic via ``guestCredential``: omit it for an authed
 * member (cookie path), pass the stored ConnectGuest credential for a
 * magic-link guest — exactly like ``askInSpace`` / ``getSpaceScope``.
 */
export interface ConnectMessageThreadProps {
  readonly spaceId: string;
  /** Magic-link guest credential — omit for an authed member (cookie path). */
  readonly guestCredential?: StoredGuestCredential | null;
  readonly className?: string;
}

export function ConnectMessageThread({
  spaceId,
  guestCredential,
  className,
}: ConnectMessageThreadProps) {
  const { i18n } = useLingui();
  const locale = i18n.locale || "it";
  const credential = guestCredential?.credential;

  const { messages, isLoading, isError } = useSpaceMessages(spaceId, credential);
  const post = usePostSpaceMessage(spaceId, credential);

  const [draft, setDraft] = React.useState("");
  // Assertive announcement of a send FAILURE (own-message data-loss guard); the
  // polite region below only carries incoming messages.
  const [sendError, setSendError] = React.useState("");
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const posting = post.isPending;

  // Auto-scroll to the newest message — but ONLY when the user is already near
  // the bottom, so we never yank someone who scrolled UP to re-read back down.
  // Mirrors ConnectChat's guarded pattern; ``scrollIntoView`` honours the OS
  // reduced-motion preference for the smooth behaviour.
  const scrollToBottom = React.useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);
  const NEAR_BOTTOM_PX = 80;
  const messageCount = messages.length;
  React.useEffect(() => {
    if (messageCount === 0) return;
    const el = scrollRef.current;
    // First paint (no scroll container yet) or already near the bottom → snap.
    const nearBottom =
      !el || el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    if (nearBottom) scrollToBottom();
  }, [messageCount, scrollToBottom]);

  const canSend = draft.trim().length > 0 && !posting;

  const submit = React.useCallback(() => {
    const body = draft.trim();
    if (!body || posting) return;
    setSendError("");
    // Do NOT clear the draft up-front: a failed POST would silently discard the
    // user's typed text. Clear only on SUCCESS; on failure surface a toast + an
    // assertive announcement and keep the text so the user can retry. Mirrors
    // how ``ConnectChat`` surfaces send errors.
    post.mutate(body, {
      onSuccess: () => {
        setDraft("");
      },
      onError: () => {
        const message = i18n._(msg`Messaggio non inviato. Riprova.`);
        toast.error(message);
        setSendError(message);
      },
    });
  }, [draft, posting, post, i18n]);

  // Dedicated screen-reader announcement of the newest INCOMING message (not
  // the viewer's own). Mirrors ConnectChat's visually-hidden live-region
  // pattern — a polite region on the whole scrolling thread announces appended
  // nodes unreliably, so we mirror the latest other-party message's plain text.
  const lastIncoming = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m && !m.is_own) return `${m.author_label}: ${m.body}`;
    }
    return "";
  }, [messages]);

  const isEmpty = !isLoading && !isError && messages.length === 0;

  return (
    <section
      className={cn(
        "flex h-full min-h-0 flex-col rounded-[14px] border border-hairline/70 bg-white/45",
        className,
      )}
      aria-label={i18n._(msg`Discussione dello spazio`)}
    >
      {/* Visually-hidden live region — announces the newest incoming message. */}
      <p className="sr-only" aria-live="polite">
        {lastIncoming}
      </p>
      {/* Assertive region — a failed send must interrupt (own text at risk). */}
      <p className="sr-only" role="alert" aria-live="assertive">
        {sendError}
      </p>

      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-5"
        aria-busy={isLoading}
      >
        {isError ? (
          <div className="m-auto flex max-w-[28rem] flex-col items-center gap-2 px-4 text-center">
            <p className="text-[13px] text-ink-2">
              <Trans>Non riusciamo a caricare la discussione. Riprova più tardi.</Trans>
            </p>
          </div>
        ) : isEmpty ? (
          <div className="m-auto flex max-w-[28rem] flex-col items-center gap-2 px-4 text-center">
            <span
              aria-hidden
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-cream-deep text-ink-3"
            >
              <MessagesSquare size={18} strokeWidth={1.75} />
            </span>
            <p className="text-[13px] text-ink-2">
              <Trans>Nessun messaggio. Scrivi il primo.</Trans>
            </p>
          </div>
        ) : (
          <>
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                variant={m.is_own ? "user" : "agent"}
                showAvatar={false}
                header={
                  <MessageMeta
                    // Own messages carry an explicit "Tu" label so identity
                    // never rests on alignment + accent colour alone — critical
                    // in a multi-party, cross-company thread.
                    author={m.is_own ? i18n._(msg`Tu`) : m.author_label}
                    timestamp={formatRelative(m.created_at, locale) ?? undefined}
                    align={m.is_own ? "end" : "start"}
                  />
                }
              >
                <span className="whitespace-pre-wrap break-words text-[13px] leading-[1.55]">
                  {m.body}
                </span>
              </MessageBubble>
            ))}
          </>
        )}
        <div ref={bottomRef} aria-hidden />
      </div>

      {/* Composer — SAME @ando/ui Composer primitives as the main chat. */}
      <div className="px-4 pb-4">
        <Composer.Root
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Composer.Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onSubmit={submit}
            disabled={posting}
            placeholder={i18n._(msg`Scrivi un messaggio…`)}
            aria-label={i18n._(msg`Messaggio alla discussione`)}
          />
          <Composer.Bar right={<Composer.SendButton canSend={canSend} onSend={submit} />} />
        </Composer.Root>
      </div>
    </section>
  );
}
ConnectMessageThread.displayName = "ConnectMessageThread";
